import React, { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, Box, Flex, Image, Icon, useDisclosure, Button } from "@chakra-ui/react";
import { NavRightActions } from "@/js/components/Nav/NavRightActions";
import { AnnouncementBar } from "@/js/components/AnnouncementBar";
import Chatbot from "@/js/components/Chatbot";
import Academy from "@/js/components/Academy/Academy";
import LogoClasswize from "@/assets/Icon_navlogo.svg?url";
import LogoSmoothwall from "@/assets/Icon_navlogoSmoothwall.svg?url";
import IconHome from "@/assets/IconHome.svg?url";
import { useThemeStore } from "@/stores/v2/themeStore";
import { getClassroomDisplayName } from "@/utils/classroomUtils";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import useStore from "@/stores/index";
import { getFeatureFlags } from "@/stores/featureTogglesStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useAcademyFeatureFlagsQuery } from "@/query/academyFeatureFlagsQuery";

type Classroom = {
	// (Slimmed down to only what this component needs)
	description: string;
	name: string;
	personalisation?: {
		displayName: string;
	};
	type: "CLASSROOM" | "GROUP";
};

interface Props {
	selectedClass?: Classroom;
}

const Nav: FunctionComponent<Props> = (props): ReactElement => {
	const navigate = useNavigate();
	const [selectedClassName, setSelectedClassName] = useState<string>("");
	const { isOpen: isAcademyDrawerOpen, onOpen: openAcademyDrawer, onClose: closeAcademyDrawer } = useDisclosure();
	const themeStore = useThemeStore();
	const academyFeatureFlagsQuery = useAcademyFeatureFlagsQuery();
	const isMasquerade = useUserSessionStore((state) => state.masquerade);
	const featureFlags = getFeatureFlags(useStore.getState());
	const isAcademyEnabled =
		(featureFlags?.["show-product-training"] ?? false) && ((academyFeatureFlagsQuery.data?.classwize_enabled ?? false) || isMasquerade);

	useEffect(() => {
		if (props.selectedClass) {
			setSelectedClassName(getClassroomDisplayName(props.selectedClass as ClassroomOverviewDTO));
		}
	}, [props.selectedClass]);

	const handleAcademyClick = async () => {
		await academyFeatureFlagsQuery.refetch();
		openAcademyDrawer();
	};

	const handleAcademyDrawerChange = (details: { open: boolean }) => {
		if (details.open) {
			openAcademyDrawer();
		} else {
			closeAcademyDrawer();
		}
	};

	return (
		<Box>
			<AnnouncementBar key={"announcement-bar"} />
			<header>
				<Flex justifyContent={"space-between"} id="navbar">
					<Flex>
						<Box
							as="button"
							onClick={() => {
								navigate("/classrooms/overview");
							}}
							className="nav-logo"
							aria-label="Classwize"
						>
							<Image src={themeStore.theme === "smoothwall" ? LogoSmoothwall : LogoClasswize} alt="" px={4} py={4} aria-hidden="true" />
						</Box>
						<Breadcrumb className="breadcrumb">
							<BreadcrumbItem>
								<BreadcrumbLink as={Link} to="/classrooms/overview">
									<Image className="NavIconHome" src={IconHome} mr={8} alt="" />
									My Classes
								</BreadcrumbLink>
							</BreadcrumbItem>
							{selectedClassName !== "" && (
								<>
									<BreadcrumbItem>
										<BreadcrumbLink className="current-link" data-test="breadcrumb-selected-classroom" isCurrentPage>
											{selectedClassName}
										</BreadcrumbLink>
									</BreadcrumbItem>
								</>
							)}
						</Breadcrumb>
					</Flex>
					<Flex alignItems="center" gap={4}>
						{isAcademyEnabled && (
							<Button
								id="academy-button"
								onClick={handleAcademyClick}
								leftIcon={<Icon as="span" className="fa-solid fa-graduation-cap" color="white" boxSize={7} />}
								aria-label="Academy"
							>
								Academy
							</Button>
						)}
						<NavRightActions classroomName={selectedClassName} />
					</Flex>
				</Flex>
			</header>
			<Chatbot />

			{isAcademyEnabled && <Academy isOpen={isAcademyDrawerOpen} onOpenChange={handleAcademyDrawerChange} />}
		</Box>
	);
};

export default Nav;
